
// components/ModifierFacture.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { useSelector } from "react-redux";
import { getClient } from "../services/ClientService";
import { getFacture, updateFacture } from "../services/FactureService";
import { getAutres, getBatiment } from "../services/SecteurServices";
import { tvainfo } from "../services/LoginService";

const ModifierFacture = () => {
    const { id } = useParams();
    const token = useSelector((state) => state.auth.token);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [tva_inclus, setTvaInclus] = useState(false);
    const [activeRates, setActiveRates] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [secteursBatiment, setSecteursBatiment] = useState([]);
    const [secteursAutres, setSecteursAutres] = useState([]);
    const [selectedBatiment, setSelectedBatiment] = useState([]);
    const [selectedAutres, setSelectedAutres] = useState([]);
    const [facture, setFacture] = useState(null);

    const [totaux, setTotaux] = useState({
        sous_total_centimes: 0,
        remise_centimes: 0,
        tva_centimes: 0,
        total_centimes: 0,
    });

    const statuts = [
        { label: "Brouillon", value: "brouillon" },
        { label: "Envoyée", value: "envoye" },
        { label: "Partiellement payée", value: "partiel" },
        { label: "Payée", value: "paye" },
        { label: "En retard", value: "retard" },
        { label: "Annulée", value: "annule" },
    ];

    const typesPaiement = [
        { label: "Virement", value: "virement" },
        { label: "Chèque", value: "cheque" },
        { label: "Espèces", value: "espece" },
    ];

    // Chargement initial
    useEffect(() => {
        if (!token) return;
        const fetchData = async () => {
            try {
                // TVA
                const resTVA = await tvainfo(token);
                if (resTVA.success) {
                    setTvaInclus(resTVA.tva_inclus ?? false);
                    const rates = resTVA.tva_rates || {};
                    const active = Object.keys(rates)
                        .filter((k) => rates[k])
                        .sort((a, b) => parseFloat(a) - parseFloat(b));
                    setActiveRates(active);
                }

                // Clients
                const clientsData = await getClient(token);
                setClients(clientsData);

                // Facture existante
                const factureData = await getFacture(id);
                setFacture(factureData);
                setSelectedClient(factureData.client);

                // Secteurs
                if (factureData.secteurs_batiment) {
                    setSelectedBatiment(factureData.secteurs_batiment.map((s) => s.id));
                }
                if (factureData.secteurs_autres) {
                    setSelectedAutres(factureData.secteurs_autres.map((s) => s.id));
                }
                const batimentRes = await getBatiment(token);
                const autresRes = await getAutres(token);
                setSecteursBatiment(batimentRes);
                setSecteursAutres(autresRes);
            } catch (err) {
                console.error(err);
                toast.error("Erreur lors du chargement des données.");
                navigate("/EspaceClient/factures");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [token, id, navigate]);

    // Recalcul des totaux à chaque modification de lignes
    // Recalcul des totaux
    useEffect(() => {
        if (!facture) return;

        let sousTotal = 0, remise = 0, tva = 0;

        facture.lignes.forEach(ligne => {
            const prix = parseFloat(ligne.prix_unitaire_centimes) || 0;
            const quantite = parseFloat(ligne.quantite) || 0;
            const remiseLigne = parseFloat(ligne.taux_remise) || 0;
            const tauxTVA = parseFloat(ligne.taux_tva) || 0;

            const ligneTotal = prix * quantite;
            const ligneRemise = (ligneTotal * remiseLigne) / 100;
            const ligneHT = ligneTotal - ligneRemise;
            const ligneTVA = (ligneHT * tauxTVA) / 100;

            sousTotal += ligneHT;
            remise += ligneRemise;
            tva += ligneTVA;
        });

        setTotaux({
            sous_total_centimes: sousTotal,
            remise_centimes: remise,
            tva_centimes: tva,
            total_centimes: sousTotal + tva,
        });
    }, [facture]); // 🔹 seulement lignes


    // TVA par taux
    const tvaParTaux = React.useMemo(() => {
        const lignes = facture?.lignes || [];
        const result = {};
        activeRates.forEach(rate => (result[rate] = 0));

        lignes.forEach(ligne => {
            const totalHT = parseFloat(ligne.total_ligne_centimes) || 0;
            const tauxTVA = (parseFloat(ligne.taux_tva) || 0).toFixed(2);
            result[tauxTVA] = (result[tauxTVA] || 0) + (totalHT * parseFloat(tauxTVA)) / 100;
        });

        return result;
    }, [facture?.lignes, activeRates]);


    useEffect(() => {
        if (!activeRates.length) return;
        setFacture(f => {
            if (!f || !f.lignes.length) return f;
            const newLignes = [...f.lignes];
            if (!newLignes[0].taux_tva || newLignes[0].taux_tva === 0) {
                newLignes[0].taux_tva = parseFloat(activeRates[0]);
            }
            return { ...f, lignes: newLignes };
        });
    }, [activeRates]); // ✅ propre, plus besoin de 'facture'


    const addLigne = () => {
        setFacture(f => ({
            ...f,
            lignes: [
                ...f.lignes,
                {
                    designation: "",
                    quantite: 1,
                    unite: "",
                    prix_unitaire_centimes: 0,
                    taux_tva: activeRates[0] ? parseFloat(activeRates[0]) : 0,
                    taux_remise: 0,
                    total_ligne_centimes: 0,
                },
            ],
        }));
    };


    const removeLigne = (index) => {
        if (facture.lignes.length <= 1) {
            toast.error("Une facture doit contenir au moins une ligne");
            return;
        }
        const newLignes = [...facture.lignes];
        newLignes.splice(index, 1);
        setFacture({ ...facture, lignes: newLignes });
    };

    const updateLigne = (index, field, value) => {
        setFacture(f => {
            const newLignes = [...f.lignes];
            newLignes[index][field] = value;

            const prix = parseFloat(newLignes[index].prix_unitaire_centimes) || 0;
            const quantite = parseFloat(newLignes[index].quantite) || 0;
            const remise = parseFloat(newLignes[index].taux_remise) || 0;

            const ligneTotal = prix * quantite;
            const ligneRemise = (ligneTotal * remise) / 100;
            newLignes[index].total_ligne_centimes = ligneTotal - ligneRemise;

            newLignes[index].taux_tva = parseFloat(newLignes[index].taux_tva) || (activeRates[0] ? parseFloat(activeRates[0]) : 0);

            return { ...f, lignes: newLignes };
        });
    };

    const handleSave = async () => {
        if (!selectedClient) {
            toast.error("Veuillez sélectionner un client.");
            return;
        }

        const lignesValides = facture.lignes.filter(
            (ligne) => ligne.designation?.trim() && parseFloat(ligne.quantite) > 0 && parseFloat(ligne.prix_unitaire_centimes) > 0
        );

        if (lignesValides.length !== facture.lignes.length) {
            toast.error("Toutes les lignes doivent avoir une désignation, une quantité >= 1 et un prix unitaire > 0.");
            return;
        }

        try {
            setLoading(true);

            await updateFacture(facture.id, {
                ...facture,
                client_id: selectedClient.id,
                sous_total_centimes: totaux.sous_total_centimes,
                remise_centimes: totaux.remise_centimes,
                tva_centimes: totaux.tva_centimes,
                total_centimes: totaux.total_centimes,
                lignes: lignesValides,
                type_paiement: facture.type_paiement || null,
                acompte_centimes: Math.min(facture.acompte_centimes, totaux.total_centimes),
                secteurs_batiment: selectedBatiment,
                secteurs_autres: selectedAutres,
            });
            setLoading(false);

            navigate("/EspaceClient/factures", { state: { message: "Facture mise à jour avec succès !" } });
        } catch (err) {
            toast.error(err.message || "Erreur lors de la mise à jour de la facture.");
            setLoading(false);

        }
    };

    if (loading)
        return (
            <div className="flex justify-center items-center mt-20">
                <LoadingIcons.SpinningCircles stroke="#2218eeff" fill="#2218eeff" strokeWidth={3} height={40} width={40} />
                <span className="ml-3 text-gray-600 font-medium">Chargement...</span>
            </div>
        );

    if (!facture) return null;

    return (
        <div className="min-h-screen p-6 flex flex-col gap-2">
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="flex justify-between items-center mb-2">
                <h1 className="font-bold text-2xl">Modifier la Facture</h1>
                <button
                    onClick={handleSave}
                    className="bg-color3 hover:bg-color3/80 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <FaSave /> Enregistrer
                </button>
            </div>

            <div className="flex flex-col gap-5">
                {/* Informations facture */}
                <div className="bg-white shadow-md rounded-lg p-4">
                    <h2 className="font-semibold mb-4">Informations de la facture</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Client</label>
                            {/* Client */}
                            <select
                                className="border rounded-md px-2 py-1"
                                value={selectedClient?.id ?? ""} // 🔹 remplacer null par ""
                                onChange={(e) =>
                                    setSelectedClient(clients.find((c) => c.id === Number(e.target.value)))
                                }
                            >
                                <option value="">-- Sélectionner --</option>
                                {clients.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.nom_affichage} ({c.email || "-"})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Statut</label>
                            {/* Statut */}
                            <select
                                className="border rounded-md px-2 py-1"
                                value={facture.statut ?? ""} // 🔹 null → ""
                                onChange={(e) => setFacture({ ...facture, statut: e.target.value })}
                            >
                                {statuts.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Type de paiement</label>
                            {/* Type de paiement */}
                            <select
                                className="border rounded-md px-2 py-1"
                                value={facture.type_paiement ?? ""} // 🔹 null → ""
                                onChange={(e) => setFacture({ ...facture, type_paiement: e.target.value })}
                            >
                                <option value="">-- Sélectionner --</option>
                                {typesPaiement.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Acompte</label>
                            {/* Acompte */}
                            <input
                                type="number"
                                min={0}
                                max={totaux.total_centimes}
                                value={facture.acompte_centimes ?? 0} // 🔹 null → 0
                                onChange={(e) =>
                                    setFacture({
                                        ...facture,
                                        acompte_centimes: Math.min(parseFloat(e.target.value) || 0, totaux.total_centimes),
                                    })
                                }
                                className="border rounded-md px-2 py-1"
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Date d'émission</label>
                            {/* Date d'émission */}
                            <input
                                type="date"
                                className="border rounded-md px-2 py-1"
                                value={facture.date_emission ?? ""} // 🔹 null → ""
                                onChange={(e) => {
                                    const newDate = e.target.value;
                                    setFacture({
                                        ...facture,
                                        date_emission: newDate,
                                        date_echeance: facture.date_echeance < newDate ? newDate : facture.date_echeance,
                                    });
                                }}
                            />
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium">Date d'échéance</label>
                            {/* Date d'échéance */}
                            <input
                                type="date"
                                className="border rounded-md px-2 py-1"
                                value={facture.date_echeance ?? ""} // 🔹 null → ""
                                min={facture.date_emission ?? ""} // 🔹 null → ""
                                onChange={(e) => setFacture({ ...facture, date_echeance: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Secteurs */}
                    <div className="bg-white shadow-md rounded-xl p-6 mb-6">
                        <h2 className="text-lg font-semibold mb-4">Secteurs</h2>

                        {/* Bâtiment */}
                        <h3 className="text-sm font-medium text-gray-600 mb-2">Bâtiment</h3>
                        <div className="flex flex-wrap gap-3 mb-4">
                            {secteursBatiment.map((s) => {
                                const isSelected = selectedBatiment.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() =>
                                            setSelectedBatiment((prev) =>
                                                prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                                            )
                                        }
                                        className={`px-3 py-1 rounded-full text-sm font-medium border transition ${isSelected
                                            ? "bg-green-600 text-white border-green-600 shadow"
                                            : "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                            }`}
                                    >
                                        {s.nom}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Autres */}
                        <h3 className="text-sm font-medium text-gray-600 mb-2">Autres</h3>
                        <div className="flex flex-wrap gap-3">
                            {secteursAutres.map((s) => {
                                const isSelected = selectedAutres.includes(s.id);
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onClick={() =>
                                            setSelectedAutres((prev) =>
                                                prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                                            )
                                        }
                                        className={`px-3 py-1 rounded-full text-sm font-medium border transition ${isSelected
                                            ? "bg-gray-700 text-white border-gray-700 shadow"
                                            : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                                            }`}
                                    >
                                        {s.nom}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Lignes facture */}
                <div className="bg-white shadow-md rounded-lg p-4 w-full">
                    <h2 className="font-semibold mb-2">Lignes de facture</h2>
                    <table className="w-full text-sm text-left border-collapse mb-4">
                        <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                            <tr>
                                <th className="px-3 py-2 text-left">Désignation</th>
                                <th className="px-3 py-2 w-24 text-center">Qté</th>
                                <th className="px-3 py-2 w-24 text-center">Unité</th>
                                <th className="px-3 py-2 w-32 text-center">Prix Unitaire</th>
                                <th className="px-3 py-2 w-24 text-center">TVA (%)</th>
                                <th className="px-3 py-2 w-24 text-center">Remise (%)</th>
                                <th className="px-3 py-2 w-32 text-center">Total HT</th>
                                {facture.lignes.length > 1 && <th className="px-3 py-2 w-16">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Lignes de facture */}
                            {facture.lignes.map((ligne, index) => (
                                <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="px-2 py-1">
                                        <textarea
                                            value={ligne.designation ?? ""} // 🔹 null → ""
                                            onChange={(e) => updateLigne(index, "designation", e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-1 py-1 resize-none"
                                            rows={4}
                                        />
                                    </td>
                                    <td className="px-2 py-1">
                                        <input
                                            type="number"
                                            min={1}
                                            step={1}
                                            value={ligne.quantite ?? 1}
                                            onChange={(e) => {
                                                let value = parseFloat(e.target.value) || 1; // parseFloat pour autoriser virgule
                                                if (value < 1) value = 1;
                                                // si la partie décimale est 0, on stocke un entier
                                                if (value % 1 === 0) value = Math.floor(value);
                                                updateLigne(index, "quantite", value);
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-1 py-1"
                                        />
                                    </td>

                                    <td className="px-2 py-1">
                                        <input
                                            type="text"
                                            value={ligne.unite ?? ""} // 🔹 null → ""
                                            onChange={(e) => updateLigne(index, "unite", e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-1 py-1"
                                        />
                                    </td>
                                    <td className="px-2 py-1">
                                        <input
                                            type="number"
                                            min={0}
                                            value={ligne.prix_unitaire_centimes ?? 0} // 🔹 null → 0
                                            onChange={(e) => updateLigne(index, "prix_unitaire_centimes", e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-1 py-1"
                                        />
                                    </td>
                                    <td className="px-2 py-1">
                                        {tva_inclus && activeRates.length > 0 ? (
                                            <select
                                                value={ligne.taux_tva ?? activeRates[0]} // 🔹 null → premier taux actif
                                                onChange={(e) => updateLigne(index, "taux_tva", parseFloat(e.target.value))}
                                                className="w-full border border-gray-300 rounded-md px-1 py-1"
                                            >
                                                {activeRates.map((taux) => (
                                                    <option key={taux} value={parseFloat(taux)}>
                                                        {taux}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <p className="text-center">-</p>
                                        )}
                                    </td>
                                    <td className="px-2 py-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={ligne.taux_remise ?? 0} // 🔹 null → 0
                                            onChange={(e) => {
                                                let value = parseFloat(e.target.value) || 0;
                                                if (value > 100) value = 100;
                                                if (value < 0) value = 0;
                                                updateLigne(index, "taux_remise", value);
                                            }}
                                            className="w-full border border-gray-300 rounded-md px-1 py-1"
                                        />
                                    </td>
                                    <td className="px-2 py-1 font-bold text-center">{(ligne.total_ligne_centimes ?? 0).toFixed(2)} €</td>
                                    {facture.lignes.length > 1 && (
                                        <td className="px-2 py-1 text-center">
                                            <button onClick={() => removeLigne(index)} className="text-red-600 hover:text-red-800">
                                                <FaTrash />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="flex justify-end mb-4">
                        <button
                            onClick={addLigne}
                            className="bg-color3 hover:bg-color3/80 text-white px-3 py-1 rounded-lg flex items-center gap-1"
                        >
                            <FaPlus /> Ajouter une ligne
                        </button>
                    </div>


                    {/* Totaux */}
                    <div className="grid grid-cols-[140px_120px] gap-y-1 justify-end items-end">
                        {/* Total HT */}
                        {totaux.sous_total_centimes > 0 && (
                            <>
                                <span className="font-bold text-left">Total HT :</span>
                                <span className="text-right">{totaux.sous_total_centimes.toFixed(2)} €</span>
                            </>
                        )}

                        {/* Remise */}
                        {totaux.remise_centimes > 0 && (
                            <>
                                <span className="font-bold text-left">Remise :</span>
                                <span className="text-right">{totaux.remise_centimes.toFixed(2)} €</span>
                            </>
                        )}

                        {/* TVA par taux */}
                        {tva_inclus &&
                            Object.keys(tvaParTaux).length > 0 &&
                            activeRates
                                .filter((rate) => (tvaParTaux[parseFloat(rate).toFixed(2)] || 0) > 0)
                                .map((rate) => {
                                    const key = parseFloat(rate).toFixed(2);
                                    return (
                                        <React.Fragment key={key}>
                                            <span className="font-bold text-left">TVA ({rate}) :</span>
                                            <span className="text-right">{tvaParTaux[key].toFixed(2)} €</span>
                                        </React.Fragment>
                                    );
                                })}

                        {/* Total TVA */}
                        {totaux.tva_centimes > 0 && (
                            <>
                                <span className="font-bold text-left">Total TVA :</span>
                                <span className="text-right">{totaux.tva_centimes.toFixed(2)} €</span>

                                {/* Total TTC */}
                                <span className="font-bold text-left">Total TTC :</span>
                                <span className="text-right">{totaux.total_centimes.toFixed(2)} €</span>
                            </>
                        )}

                        {/* Acompte */}
                        {facture.acompte_centimes > 0 && (
                            <>
                                <span className="font-bold text-left text-green-600">Acompte :</span>
                                <span className="text-right text-green-600 font-semibold">{facture.acompte_centimes.toFixed(2)} €</span>

                                {/* Net à payer */}
                                {tva_inclus && totaux.total_centimes > 0 && (
                                    <>
                                        <span className="font-bold text-left">Reste à payer :</span>
                                        <span className="text-right">{(totaux.total_centimes - facture.acompte_centimes).toFixed(2)} €</span>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                </div>
            </div>
        </div>

    );
};

export default ModifierFacture;
